/**
 * Single source of truth for Cognito tokens in the browser (localStorage, shared across tabs).
 */

const KEYS = {
  idToken: 'dooglie.auth.idToken',
  refreshToken: 'dooglie.auth.refreshToken',
} as const;

/**
 * No access token. Cognito's is not what anything here authenticates with — the
 * API sends the *id* token (see `getAuthorizationHeaderValue`) and the extension
 * is handed the id and refresh tokens. The desktop bridge page was the only
 * reader the access token ever had, and it is gone.
 */
export interface AuthTokens {
  readonly idToken: string;
  readonly refreshToken?: string;
}

export function getTokens(): Partial<AuthTokens> {
  const idToken = localStorage.getItem(KEYS.idToken) ?? undefined;
  const refreshToken = localStorage.getItem(KEYS.refreshToken) ?? undefined;
  return { idToken, refreshToken };
}

export function setTokens(tokens: AuthTokens): void {
  localStorage.setItem(KEYS.idToken, tokens.idToken);
  if (tokens.refreshToken) {
    localStorage.setItem(KEYS.refreshToken, tokens.refreshToken);
  } else {
    localStorage.removeItem(KEYS.refreshToken);
  }
}

export function clearSession(): void {
  localStorage.removeItem(KEYS.idToken);
  localStorage.removeItem(KEYS.refreshToken);
  // Stored until the desktop bridge was removed, and read by nothing since.
  // Here to clear the orphan out of existing sessions; delete after a release.
  localStorage.removeItem('dooglie.auth.accessToken');
}

/** Same value previously sent as Authorization (raw JWT, no Bearer prefix). */
export function getAuthorizationHeaderValue(): string | undefined {
  const { idToken } = getTokens();
  return idToken;
}

function decodeJwtExpSeconds(jwt: string): number | null {
  try {
    const parts = jwt.split('.');
    if (parts.length < 2) return null;
    const payload = parts[1];
    const json = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
    const { exp } = JSON.parse(json) as { exp?: number };
    return typeof exp === 'number' ? exp : null;
  } catch {
    return null;
  }
}

const CLOCK_SKEW_SECONDS = 60;

export function isIdTokenExpired(idToken: string): boolean {
  const exp = decodeJwtExpSeconds(idToken);
  if (exp == null) return true;
  return Date.now() / 1000 >= exp - CLOCK_SKEW_SECONDS;
}

export function hasValidIdToken(): boolean {
  const { idToken } = getTokens();
  return !!idToken && !isIdTokenExpired(idToken);
}
